import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { useBlocker } from 'react-router-dom';
import { BaseButton, Spinner } from '../base';
import { mdiDownload, mdiOpenInNew, mdiRotateLeft, mdiRotateRight } from '@mdi/js';
import Icon from '@mdi/react';
import { getFileTypeInfo, isPdfFile, isImageFile, FileTypeInfo } from '../../utils/file-type-utils';
import * as S from './document-viewer.styles';

export interface DocumentViewerProps {
    url: string;
    documentName?: string;
    metadata?: string;
    enableEditing?: boolean;
    loading?: boolean;
    overlays?: any[];
    onOverlayClick?: (key: string) => void;
    onMetadataUpdated?: (metadata: string) => Promise<void>;
    onDocumentUpdated?: (buffer: ArrayBuffer) => Promise<void>;
    onParseDocument?: () => Promise<void>;
}

export type DocumentMetadata = any;
export type OverlayMetadata = {
    height: number;
    left: number;
    top: number;
    width: number;
    pageIndex: number;
    key: string;
};

const DocumentViewerWithBlocker: React.FC<DocumentViewerProps> = props => {
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => !!props.loading && currentLocation.pathname !== nextLocation.pathname,
    );

    useEffect(() => {
        if (blocker && blocker.state === 'blocked') {
            if (
                window.confirm(
                    'The document is still loading. If you leave this page, your changes may not be saved. Do you still want to leave the page?',
                )
            ) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);

    return <DocumentViewer {...props} />;
};

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
    url,
    documentName,
    metadata,
    enableEditing,
    loading,
    overlays = [],
    onOverlayClick,
    onMetadataUpdated,
    onDocumentUpdated,
    onParseDocument,
}) => {
    const fileInfo: FileTypeInfo = getFileTypeInfo(url, documentName);
    const [imageRotation, setImageRotation] = useState<number>(0);
    const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
    const [imageViewportSize, setImageViewportSize] = useState<{ width: number; height: number } | null>(null);
    const imageViewportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!imageViewportRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setImageViewportSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                });
            }
        });

        observer.observe(imageViewportRef.current);
        return () => observer.disconnect();
    }, []);

    const handleOpenInNewTab = () => {
        window.open(url, '_blank');
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName || 'document';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImageRotateLeft = () => {
        setImageRotation(prev => (prev - 90 + 360) % 360);
    };

    const handleImageRotateRight = () => {
        setImageRotation(prev => (prev + 90) % 360);
    };

    const isQuarterTurn = imageRotation % 180 !== 0;
    const imageLayout = useMemo(() => {
        if (!imageNaturalSize || !imageViewportSize || imageViewportSize.width <= 0 || imageViewportSize.height <= 0) {
            return null;
        }

        const baseScale = Math.min(
            imageViewportSize.width / imageNaturalSize.width,
            imageViewportSize.height / imageNaturalSize.height,
        );

        const displayedWidth = imageNaturalSize.width * baseScale;
        const displayedHeight = imageNaturalSize.height * baseScale;

        if (!isQuarterTurn) {
            return {
                width: displayedWidth,
                height: displayedHeight,
                transform: `rotate(${imageRotation}deg)`,
            };
        }

        const rotatedWidth = displayedHeight;
        const rotatedHeight = displayedWidth;
        const quarterTurnScale = Math.min(
            imageViewportSize.width / rotatedWidth,
            imageViewportSize.height / rotatedHeight,
            1,
        );

        return {
            width: displayedWidth,
            height: displayedHeight,
            transform: `rotate(${imageRotation}deg) scale(${quarterTurnScale})`,
        };
    }, [imageNaturalSize, imageViewportSize, isQuarterTurn, imageRotation]);

    const renderPdfViewer = () => {
        const defaultLayoutPluginInstance = defaultLayoutPlugin({
            sidebarTabs: defaultTabs => [defaultTabs[0], defaultTabs[1]],
        });

        return (
            <S.PdfContainer>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer fileUrl={url} plugins={[defaultLayoutPluginInstance]} />
                </Worker>
            </S.PdfContainer>
        );
    };

    const renderImageViewer = () => {
        return (
            <S.ImageContainer>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '4px',
                        width: '100%',
                        padding: '4px 16px',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#fff',
                        boxSizing: 'border-box',
                    }}>
                    <S.RotateButton size="small" variant="outlined" onClick={handleImageRotateLeft}>
                        <Icon path={mdiRotateLeft} size={0.75} />
                    </S.RotateButton>
                    <S.RotateButton size="small" variant="outlined" onClick={handleImageRotateRight}>
                        <Icon path={mdiRotateRight} size={0.75} />
                    </S.RotateButton>
                </div>
                <div
                    ref={imageViewportRef}
                    style={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        padding: '12px',
                        boxSizing: 'border-box',
                    }}>
                    <img
                        src={url}
                        alt={documentName || 'Document'}
                        onLoad={event => {
                            const target = event.currentTarget;
                            setImageNaturalSize({
                                width: target.naturalWidth,
                                height: target.naturalHeight,
                            });
                        }}
                        style={{
                            width: imageLayout ? `${imageLayout.width}px` : 'auto',
                            height: imageLayout ? `${imageLayout.height}px` : 'auto',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            objectFit: 'contain',
                            transform: imageLayout ? imageLayout.transform : `rotate(${imageRotation}deg)`,
                            transformOrigin: 'center center',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </div>
            </S.ImageContainer>
        );
    };

    const renderContent = () => {
        if (isPdfFile(fileInfo)) {
            return renderPdfViewer();
        } else if (isImageFile(fileInfo)) {
            return renderImageViewer();
        } else {
            return (
                <S.UnsupportedContainer>
                    <S.UnsupportedMessage>
                        <h3>File format not supported for inline viewing</h3>
                        <p>This file type ({fileInfo.extension || 'unknown'}) cannot be displayed inline.</p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <BaseButton variant="primary" onClick={handleDownload}>
                                <Icon path={mdiDownload} size={0.75} />
                                Download
                            </BaseButton>
                            <BaseButton variant="outlined" onClick={handleOpenInNewTab}>
                                <Icon path={mdiOpenInNew} size={0.75} />
                                Open in New Tab
                            </BaseButton>
                        </div>
                    </S.UnsupportedMessage>
                </S.UnsupportedContainer>
            );
        }
    };

    return (
        <S.Container>
            {loading && (
                <S.LoadingContainer>
                    <Spinner />
                </S.LoadingContainer>
            )}
            {renderContent()}
        </S.Container>
    );
};

export { DocumentViewerWithBlocker };
export default DocumentViewer;
